import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Drawer, Field, Select } from '@/components/live';
import { createRow } from '@/api/resources';
import { toast } from '@/lib/toast';

/**
 * One step of the traceability model (owner, 2026-09-26: "when we click + button at plantation,
 * need to get selection of entity, process type, input, output").
 *
 * A step is a statement about the SHAPE of a chain - "at processing, a sawmill turns logs into
 * sawn timber" - not a record of something that happened. The consignments and process runs that
 * follow will each point at the step they are an instance of, which is what lets the system say
 * "this batch skipped seasoning" rather than only failing to mention seasoning at all.
 *
 * THE ENTITY IS OPTIONAL and deliberately so. A client knows a sawmill saws before it knows which
 * sawmill, and demanding a party here would mean inventing one to get past the form. Naming it
 * later turns the template into a commitment.
 *
 * The process list is filtered to what can plausibly happen at the chosen stage. It is a filter,
 * not a rule: the database accepts any of them at any stage, because a client that genuinely
 * grades at the plantation should not be told by a dropdown that it does not.
 */

const PROCESSES = [
  { id: 'nursery_raising', label: 'Raising seedlings', groups: ['inputs'] },
  { id: 'planting', label: 'Planting', groups: ['plantation', 'services'] },
  { id: 'tending', label: 'Tending', groups: ['plantation', 'services'] },
  { id: 'harvesting', label: 'Harvesting', groups: ['plantation', 'services'] },
  { id: 'collection', label: 'Collection / aggregation', groups: ['intermediaries'] },
  { id: 'transport', label: 'Transport', groups: ['intermediaries', 'services'] },
  { id: 'grading', label: 'Grading', groups: ['intermediaries', 'processing'] },
  { id: 'sawing', label: 'Sawing', groups: ['processing'] },
  { id: 'seasoning', label: 'Seasoning', groups: ['processing'] },
  { id: 'treatment', label: 'Chemical treatment', groups: ['processing'] },
  { id: 'packing', label: 'Packing', groups: ['processing', 'buyers'] },
  { id: 'storage', label: 'Storage', groups: ['intermediaries', 'processing', 'buyers'] },
  { id: 'sale', label: 'Sale', groups: ['plantation', 'intermediaries', 'processing', 'buyers'] },
  { id: 'export', label: 'Export', groups: ['buyers'] },
];

/** Timber only (owner, 2026-09-26). NTFP is out of scope and is not offered rather than hidden. */
const PRODUCTS = [
  { id: 'seedlings', label: 'Seedlings' },
  { id: 'standing_trees', label: 'Standing trees' },
  { id: 'logs', label: 'Logs' },
  { id: 'poles', label: 'Poles' },
  { id: 'pulpwood', label: 'Pulpwood' },
  { id: 'fuelwood', label: 'Fuelwood' },
  { id: 'bamboo', label: 'Bamboo' },
  { id: 'sawn_timber', label: 'Sawn timber' },
  { id: 'seasoned_timber', label: 'Seasoned timber' },
  { id: 'chips', label: 'Chips' },
  { id: 'finished_goods', label: 'Finished goods' },
];

/** Processes that move or hold material without changing it, so in and out may match. */
const NON_TRANSFORMING = new Set(['transport', 'storage', 'sale', 'export']);

const STAGE_LABEL: Record<string, string> = {
  inputs: 'Inputs', services: 'Services', plantation: 'Plantation',
  intermediaries: 'Intermediaries', processing: 'Processing', buyers: 'Buyers',
};

export function ChainStepEditor({
  group, entities, onClose,
}: {
  /** The stage the + was pressed on, or null when the editor is shut. */
  group: string | null;
  entities: Array<{ id: string; display_name: string; party_code: string | null }>;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [processType, setProcessType] = useState('');
  const [partyId, setPartyId] = useState('');
  const [inputProduct, setInputProduct] = useState('');
  const [outputProduct, setOutputProduct] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();

  // A fresh step each time the editor opens, rather than the last one's answers.
  useEffect(() => {
    if (!group) return;
    setProcessType('');
    setPartyId('');
    setInputProduct('');
    setOutputProduct('');
    setNotes('');
    setError(undefined);
  }, [group]);

  const forStage = PROCESSES.filter((p) => !group || p.groups.includes(group));

  // The same rule the database enforces, said here so it is caught before a round trip rather
  // than coming back as a constraint name.
  const sameProduct = Boolean(inputProduct) && inputProduct === outputProduct
    && !NON_TRANSFORMING.has(processType);

  async function save() {
    if (!group || !processType) return;
    setError(undefined);
    setSaving(true);
    try {
      await createRow('chain_step_templates', {
        chain_group: group,
        process_type: processType,
        party_id: partyId || null,
        input_product: inputProduct || null,
        output_product: outputProduct || null,
        notes: notes.trim() || null,
      });
      // `useRows` caches under the VIEW name, so that is the key to invalidate. Inventing a
      // name here saved the row and left the chart showing the old count - the save looked
      // like it had failed when it had not.
      await queryClient.invalidateQueries({ queryKey: ['chain_step_templates'] });
      toast.success('Step added to the model.');
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That step could not be saved.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Drawer
      footer={(
        <>
          <button className="cancel" onClick={onClose} type="button">Cancel</button>
          <button
            className="primary"
            disabled={saving || !processType || sameProduct}
            onClick={() => void save()}
            type="button"
          >
            {saving ? 'Saving...' : 'Add step'}
          </button>
        </>
      )}
      onClose={onClose}
      open={Boolean(group)}
      subtitle="What is meant to happen here. Movements recorded later will point back at this step."
      title={`Add a step at ${group ? STAGE_LABEL[group] ?? group : ''}`}
    >
      <Field hint="What happens at this stage." label="Process" required>
        <Select
          onChange={setProcessType}
          options={forStage}
          placeholder="Choose a process"
          value={processType}
        />
      </Field>

      <Field
        hint="Leave empty while the step is still a statement about the chain rather than a commitment to one company."
        label="Entity"
      >
        <Select
          onChange={setPartyId}
          options={entities.map((e) => ({
            id: e.id,
            label: e.party_code ? `${e.display_name} (${e.party_code})` : e.display_name,
          }))}
          placeholder="Any actor at this stage"
          value={partyId}
        />
      </Field>

      <div className="field-row">
        <Field hint="What arrives. Empty if this step starts the chain." label="Input">
          <Select onChange={setInputProduct} options={PRODUCTS} placeholder="Nothing tracked" value={inputProduct} />
        </Field>
        <Field hint="What leaves. Empty if nothing is followed further." label="Output">
          <Select onChange={setOutputProduct} options={PRODUCTS} placeholder="Nothing tracked" value={outputProduct} />
        </Field>
      </div>

      {sameProduct && (
        <p className="field-error" role="alert">
          {STAGE_LABEL[group ?? ''] ?? 'This step'} turns a product into itself. Sawing, seasoning
          and treatment must change what they handle; if this step only moves or holds material,
          choose transport or storage.
        </p>
      )}

      <Field label="Notes">
        <textarea onChange={(event) => setNotes(event.target.value)} rows={2} value={notes} />
      </Field>

      {error && <p className="field-error" role="alert">{error}</p>}
    </Drawer>
  );
}
